/* eslint-disable no-plusplus */

/* global _OL_ORIGIN */
import Immutable from 'immutable';
import { createSelector } from 'reselect';
import urlParser from 'url';
import { enrolmentsHistorySelector } from '../../Enrolments/EnrolmentsHistory/selectors';
import { enrolmentsSelector } from '../../Enrolments/selectors';
import {
  makeSelectWhiteLabelInstitution,
  selectInstitutions,
} from '../../Institutions/selectors';
import { usersSelector } from '../../Users/selectors';
import {
  feedSearchBarUserInputSelector,
  myCoursesQueryTextSelector,
} from '../CoursesList/selectors';
import { coursesSelector } from '../selectors';

const sortByCourses = (byCourses) => {
  return byCourses.sort((a, b) => {
    return a
      .getIn(['course', 'name'])
      .localeCompare(b.getIn(['course', 'name']));
  });
};

const sortByCreators = (byCreators) => {
  return byCreators
    .sort((a, b) => {
      const getName = (item) => {
        if (item.get('institution')) {
          return item.getIn(['institution', 'name']);
        }
        return item.getIn(['creator', 'fullName']);
      };
      return getName(a).localeCompare(getName(b));
    })
    .map((createdBy) => {
      let innerSorted = createdBy;
      if (createdBy.has('courses')) {
        innerSorted = innerSorted.set(
          'courses',
          sortByCourses(createdBy.get('courses').toList())
        );
      }
      return innerSorted;
    });
};

const addCohort = (course, pathToCourse, _cohortsByCreator) => {
  let cohortsByCreator = _cohortsByCreator;
  if (!cohortsByCreator.hasIn(pathToCourse)) {
    cohortsByCreator = cohortsByCreator.setIn(
      pathToCourse,
      Immutable.fromJS({ course })
    );
  }
  return cohortsByCreator;
};

export const createdBySelector = createSelector(
  enrolmentsSelector,
  coursesSelector,
  usersSelector,
  selectInstitutions,
  makeSelectWhiteLabelInstitution,
  (enrolments, courses, users, institutions, whiteLabelInstitution) => {
    let byCreator = Immutable.Map();
    byCreator = enrolments
      .reduce((cohortsByCreatorMap, enrolment) => {
        if (!enrolment.get('display')) {
          return cohortsByCreatorMap;
        }
        const course = courses.get(enrolment.get('coursePath'));
        const creator = Immutable.fromJS(users.get(course.get('createdBy')));
        const institutionPath = enrolment.get('institutionPath');
        const institution = institutions.get(institutionPath);
        let cohortsByCreator = cohortsByCreatorMap;
        const createdBy = institutionPath || course.get('createdBy');
        if (!cohortsByCreator.has(createdBy)) {
          cohortsByCreator = cohortsByCreator.set(
            createdBy,
            Immutable.fromJS({
              creator,
              institution,
              courses: {},
            })
          );
        }
        const pathToCourse = [createdBy, 'courses', course.get('id')];
        cohortsByCreator = addCohort(course, pathToCourse, cohortsByCreator);
        return cohortsByCreator;
      }, byCreator)
      .toList();

    // apply filter if it is a whitelabel app
    if (whiteLabelInstitution && whiteLabelInstitution.get('institutionPath')) {
      byCreator = byCreator
        .reduce((currentCreator, creator, index) => {
          const newCourses = creator
            .get('courses')
            .filter(
              (course) =>
                course.getIn(['course', 'institution']) ===
                whiteLabelInstitution.get('institutionPath')
            );
          return currentCreator.setIn([index, 'courses'], newCourses);
        }, byCreator)
        .filter((creator) => {
          return creator.get('courses').size > 0;
        });
    }
    return sortByCreators(byCreator);
  }
);

export const newCreatedBySelector = createSelector(
  enrolmentsSelector,
  enrolmentsHistorySelector,
  coursesSelector,
  usersSelector,
  selectInstitutions,
  (enrolments, enrolmentshistory, courses, users, institutions) => {
    let byCreator = Immutable.Map();
    const enrolmentshistorykeys = enrolmentshistory.keySeq().toArray();
    let newEnrolments = enrolments;
    for (let i = 0; i < enrolmentshistorykeys.length; i++) {
      newEnrolments = newEnrolments.remove(enrolmentshistorykeys[i]);
    }
    byCreator = newEnrolments.reduce((cohortsByCreatorMap, enrolment) => {
      if (!enrolment.get('display')) {
        return cohortsByCreatorMap;
      }
      const course = courses.get(enrolment.get('coursePath'));
      const creator = Immutable.fromJS(users.get(course.get('createdBy')));
      const institutionPath = enrolment.get('institutionPath');
      const institution = institutions.get(institutionPath);
      let cohortsByCreator = cohortsByCreatorMap;
      const createdBy = institutionPath || course.get('createdBy');
      if (!cohortsByCreator.has(createdBy)) {
        cohortsByCreator = cohortsByCreator.set(
          createdBy,
          Immutable.fromJS({
            creator,
            institution,
            courses: {},
          })
        );
      }
      const pathToCourse = [createdBy, 'courses', course.get('id')];
      cohortsByCreator = addCohort(course, pathToCourse, cohortsByCreator);
      return cohortsByCreator;
    }, byCreator);
    return sortByCreators(byCreator.toList());
  }
);

export const selectCohortsByCreatorOrInstitution = createSelector(
  createdBySelector,
  (createdBy) => {
    return createdBy;
  }
);

export const selectNewCohortsByCreatorOrInstitution = createSelector(
  newCreatedBySelector,
  (createdBy) => {
    return createdBy;
  }
);

export const constructCourseData = (course) => {
  const courseUrl = course.getIn(['course', 'url'], _OL_ORIGIN);
  const { hostname } = urlParser.parse(courseUrl);

  return Immutable.fromJS({
    url: courseUrl,
    host: `https://${hostname}`,
    admins: course.getIn(['course', 'admins'], Immutable.List()),
    isOffline: course.getIn(['course', 'isOffline'], true),
    courseName: course.getIn(['course', 'name'], ''),
    coursePath: course.getIn(['course', 'path'], ''),
    courseHomePage: course.getIn(['course', 'courseHomepage'], ''),
    cohortName: course.getIn(['course', 'name'], 'Unknown course name'),
    cohortPath: course.getIn(
      ['course', 'defaultCohort'],
      'Unknown cohort path'
    ),
    cohortImagePath: course.getIn(['course', 'promoImage'], ''),
    cohortThemeColor: course.getIn(
      ['course', 'themeData', 'theme-color'],
      '#61BEB2'
    ),
    institution: course.getIn(['course', 'institution'], '')
      ? course.getIn(['course', 'institution'], '')
      : '',
    certification: course.getIn(['course', 'certification']),
  });
};

function arrangeData(cohorts, result, querytext) {
  let stickyHeaderIndices = Immutable.List();
  if (querytext === null) {
    // eslint-disable-next-line no-param-reassign
    querytext = '';
  }
  const trimQuery = querytext.trim();
  const regex = new RegExp(
    trimQuery.replace(/([.?*+^$[\]\\(){}|-])/g, '\\$1'),
    'i'
  );
  let cohortsList = result;
  cohorts.map((data) => {
    stickyHeaderIndices = stickyHeaderIndices.push(cohortsList.size);
    const filteredData = data
      .get('courses')
      .filter((x) => x.getIn(['course', 'name']).search(regex) >= 0);

    if (filteredData.size > 0) {
      cohortsList = cohortsList.push(
        Immutable.fromJS({
          creator:
            data.getIn(['institution', 'name']) ||
            data.getIn(['creator', 'fullName'], 'Unknown creator'),
        })
      );
    }
    return filteredData.map((course) => {
      return (cohortsList = cohortsList.push(constructCourseData(course)));
    });
  });
  return Immutable.fromJS({
    cohortsList,
    stickyHeaderIndices,
  });
}

export const selectCourseListForCourseFeed = createSelector(
  selectCohortsByCreatorOrInstitution,
  (cohorts) => {
    const result = Immutable.fromJS([]);
    return arrangeData(cohorts, result, '');
  }
);

export const selectFiltersForFeed = createSelector(
  feedSearchBarUserInputSelector,
  selectCohortsByCreatorOrInstitution,
  (userInput, cohorts) => {
    const result = Immutable.fromJS([]);
    return arrangeData(cohorts, result, userInput);
  }
);

export const selectCourseListForMyCourse = createSelector(
  myCoursesQueryTextSelector,
  selectCohortsByCreatorOrInstitution,
  (querytext, cohorts) => {
    const result = Immutable.fromJS([]);
    return arrangeData(cohorts, result, querytext);
  }
);

export const selectEnrolledCoursePathList = createSelector(
  selectCourseListForMyCourse,
  (courseList) => {
    const coursePathList = courseList
      .get('cohortsList')
      .map((course) => course.get('coursePath'));
    return Immutable.Map({
      coursePathList,
    });
  }
);

export const selectFiltersForMyCourse = createSelector(
  selectCohortsByCreatorOrInstitution,
  (cohorts) => {
    let result = Immutable.fromJS([]);
    cohorts.map((data) => {
      return data.get('courses').map((course) => {
        return (result = result.push(
          Immutable.fromJS({
            key: course.getIn(['course', 'name'], 'Unknown course name'),
            value: course.getIn(['course', 'name'], 'Unknown course name'),
          })
        ));
      });
    });
    return result;
  }
);

export const selectFiltersForNewCourse = createSelector(
  selectNewCohortsByCreatorOrInstitution,
  (cohorts) => {
    let result = Immutable.fromJS([]);
    cohorts.map((data) => {
      return data.get('courses').map((course) => {
        return (result = result.push(
          Immutable.fromJS({
            key: course.getIn(['course', 'name'], 'Unknown course name'),
            value: course.getIn(['course', 'name'], 'Unknown course name'),
          })
        ));
      });
    });
    return result;
  }
);

export const makeSelectCourseDetailForPostDetail = (coursePath) =>
  createSelector(
    coursesSelector,
    (courses) => {
      let course = Immutable.Map();
      course = course.set('course', courses.get(coursePath, Immutable.Map()));
      return constructCourseData(course);
    }
  );
